// src/modulos/Parametros/TargetAudiencePage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const TARGET_AUDIENCE_COLLECTION = 'parameters/data/publico-alvo';
const ITEMS_PER_PAGE = 10;

const TargetAudiencePage = () => {
    const can = usePagePermissions('publico_alvo');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [usageInfo, setUsageInfo] = useState(null);

    // Hook de Dados
    const {
        data: targetAudiences,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(TARGET_AUDIENCE_COLLECTION, 'name');

    // Buscar produtos para verificar vínculos
    const { data: products } = useFirestore('products', 'name');

    // ============================================================
    // 1. DEFINIR filteredAudiences ANTES dos callbacks
    // ============================================================
    const filteredAudiences = useMemo(() => {
        if (!targetAudiences) return [];
        if (!searchTerm) return targetAudiences;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return targetAudiences.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [targetAudiences, searchTerm]);

    const totalFilteredItems = filteredAudiences.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentAudiences = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredAudiences.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredAudiences, currentPage]);

    // Verificar se um público-alvo está sendo utilizado
    const checkAudienceUsage = useCallback((audienceId, audienceName) => {
        if (!products || products.length === 0) {
            return { used: false, count: 0, products: [] };
        }

        const linkedProducts = products.filter(product =>
            product.targetAudienceId === audienceId ||
            product.targetAudience === audienceName
        );

        return {
            used: linkedProducts.length > 0,
            count: linkedProducts.length,
            products: linkedProducts
        };
    }, [products]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
        setUsageInfo(null);
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        const usage = checkAudienceUsage(id, name);

        if (usage.used) {
            setUsageInfo(usage);
            setDeleteConfirm({ isOpen: true, id, name });
        } else {
            setDeleteConfirm({ isOpen: true, id, name });
            setUsageInfo(null);
        }
    }, [checkAudienceUsage]);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            // Se tiver uso e o usuário confirmou, deletar mesmo assim
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Público-Alvo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Público-Alvo: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar público-alvo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
            setUsageInfo(null);
        }
    }, [deleteConfirm, deleteDocument, totalFilteredItems, currentPage]);

    // Verificar duplicados
    const isDuplicateName = useCallback((name, excludeId = null) => {
        const lowerCaseName = name.trim().toLowerCase();
        return targetAudiences?.some(item =>
            item.name.toLowerCase() === lowerCaseName &&
            item.id !== excludeId
        );
    }, [targetAudiences]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        const trimmedName = nameInput.trim();

        if (!trimmedName) {
            setFormError("O nome do Público-Alvo é obrigatório.");
            return;
        }

        if (isDuplicateName(trimmedName, editingItem?.id)) {
            setFormError("Este público-alvo já está cadastrado. Por favor, utilize um nome diferente.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: trimmedName,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Público-Alvo editado com sucesso!' : 'Público-Alvo adicionado com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, isDuplicateName, addDocument, updateDocument, resetForm]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Público-Alvo'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Público-Alvo <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Ex: Pessoa Física, Pessoa Jurídica, Revenda"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome único que identifique este segmento de público.
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title={usageInfo?.used ? "⚠️ Atenção: Público-Alvo em Uso" : "⚠️ Confirmar Exclusão"}
                message={
                    usageInfo?.used
                        ? `O público-alvo "${deleteConfirm.name}" está vinculado a ${usageInfo.count} produto(s). Deseja deletar mesmo assim?`
                        : `Tem certeza que deseja deletar o público-alvo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`
                }
                isLoading={deleteLoading}
                danger={true}
            >
                {usageInfo?.used && (
                    <>
                        <p style={{ margin: '8px 0', color: 'var(--text-secondary)' }}>
                            <strong>Produtos vinculados:</strong>
                        </p>
                        <ul style={{ marginBottom: '16px', paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                            {usageInfo.products.slice(0, 5).map(product => (
                                <li key={product.id}>{product.name || product.title || 'Produto sem nome'}</li>
                            ))}
                            {usageInfo.count > 5 && <li>...e mais {usageInfo.count - 5} produto(s)</li>}
                        </ul>
                        <div className={`${styles.alert} ${styles.warning}`} style={{ margin: '16px 0' }}>
                            <span className={styles.alertIcon}>⚠️</span>
                            <strong>ATENÇÃO:</strong> Ao deletar este público-alvo, os produtos vinculados perderão esta referência
                            e podem não ser mais encontrados nas buscas por segmentação.
                        </div>
                    </>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Público-Alvo"
                actionText="Adicionar Público-Alvo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome do público-alvo..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentAudiences.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '👥'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum público-alvo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? 'Tente outro termo de busca'
                                    : "Clique em 'Adicionar Público-Alvo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Público-Alvo</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentAudiences.map((item) => {
                                            const usage = checkAudienceUsage(item.id, item.name);
                                            return (
                                                <tr key={item.id} className={styles.tableRow}>
                                                    <td className={styles.conditionName}>
                                                        <strong>{item.name}</strong>
                                                        {usage.used && (
                                                            <small className={styles.rowMetadata}>
                                                                Usado em {usage.count} produto(s)
                                                            </small>
                                                        )}
                                                        {item.updatedAt && (
                                                            <small className={styles.rowMetadata}>
                                                                Atualizado: {new Date(item.updatedAt).toLocaleDateString()}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td className={styles.actionsCell}>
                                                        {can.edit && (
                                                            <button
                                                                onClick={() => handleEditClick(item)}
                                                                className={styles.actionButton}
                                                                disabled={formLoading}
                                                            >
                                                                Editar
                                                            </button>
                                                        )}
                                                        {can.delete && (
                                                            <button
                                                                onClick={() => handleDeleteClick(item.id, item.name)}
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={formLoading}
                                                            >
                                                                Deletar
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="itens"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TargetAudiencePage;